import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Package, Calendar, Users, Truck, Mail, MoreVertical, Filter, Download, BarChart3 } from 'lucide-react'
import { deliveryApi } from '../services/api'
import { showError } from '../utils/showError'
import {
  PageHeader,
  SearchBar,
  ListContainer,
  Pagination,
  LoadingSpinner,
  EmptyState,
  Button,
  Card,
  CardBody,
  FormSelect,
  FormField,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  StatusBadge
} from '../components/common'

const Deliveries = () => {
  const [deliveries, setDeliveries] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchInput, setSearchInput] = useState('') // Separate input state for typing
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [filters, setFilters] = useState({
    deliveryType: '',
    buildingId: '',
    isDelivered: '',
    isCollected: '',
    fromDate: '',
    toDate: '',
    recipientUserId: ''
  })
  const [selectedDeliveries, setSelectedDeliveries] = useState([])
  const [showBulkActions, setShowBulkActions] = useState(false)
  const [buildings, setBuildings] = useState([])
  const [activeFilter, setActiveFilter] = useState('all') // all, overdue, pending, awaiting-collection

  const pageSize = 10

  const loadDeliveries = async () => {
    try {
      setLoading(true)
      const params = {
        pageNumber: currentPage,
        pageSize,
        ...(searchTerm && { searchString: searchTerm }),
        ...(filters.deliveryType && { deliveryType: parseInt(filters.deliveryType) }),
        ...(filters.buildingId && { buildingId: filters.buildingId }),
        ...(filters.isDelivered !== '' && { isDelivered: filters.isDelivered === 'true' }),
        ...(filters.isCollected !== '' && { isCollected: filters.isCollected === 'true' }),
        ...(filters.fromDate && { fromDate: filters.fromDate }),
        ...(filters.toDate && { toDate: filters.toDate }),
        ...(filters.recipientUserId && { recipientUserId: filters.recipientUserId })
      }

      let response
      switch (activeFilter) {
        case 'overdue':
          response = await deliveryApi.getOverdue(params)
          break
        case 'pending':
          response = await deliveryApi.getPending(params)
          break
        case 'awaiting-collection':
          response = await deliveryApi.getAwaitingCollection(params)
          break
        default:
          response = await deliveryApi.list(params)
      }

      console.log('Deliveries API response:', response.data)
      setDeliveries(response.data.items || [])
      setTotalPages(response.data.totalPages || 1)
      setTotalCount(response.data.totalCount || 0)
    } catch (error) {
      console.error('Error loading deliveries:', error)
      setDeliveries([])
      setTotalPages(1)
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }

  const loadBuildings = async () => {
    try {
      const response = await deliveryApi.list({ pageSize: 1000 }) // Get buildings from delivery data
      const uniqueBuildings = response.data.items?.reduce((acc, delivery) => {
        if (delivery.buildingId && delivery.buildingName && !acc.find(b => b.id === delivery.buildingId)) {
          acc.push({ id: delivery.buildingId, name: delivery.buildingName })
        }
        return acc
      }, []) || []
      setBuildings(uniqueBuildings)
    } catch (error) {
      console.error('Error loading buildings:', error)
    }
  }

  useEffect(() => {
    loadDeliveries()
  }, [currentPage, searchTerm, filters, activeFilter])

  useEffect(() => {
    loadBuildings()
  }, [])

  // Debounced search with proper cleanup
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput)
        setCurrentPage(1)
      }
    }, 500) // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId)
  }, [searchInput, searchTerm])

  const handleSearch = (term) => {
    setSearchInput(term) // Update input immediately for UI responsiveness
  }

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      setSearchTerm(searchInput) // Immediate search on Enter
      setCurrentPage(1)
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setCurrentPage(1)
  }

  const handleActiveFilterChange = (filter) => {
    setActiveFilter(filter)
    setCurrentPage(1)
  }

  const handleSelectDelivery = (deliveryId, isSelected) => {
    if (isSelected) {
      setSelectedDeliveries(prev => [...prev, deliveryId])
    } else {
      setSelectedDeliveries(prev => prev.filter(id => id !== deliveryId))
    }
  }

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedDeliveries(deliveries.map(d => d.deliveryId))
    } else {
      setSelectedDeliveries([])
    }
  }

  const handleBulkStatusUpdate = async (isDelivered, isCollected) => {
    try {
      await deliveryApi.bulkUpdateStatus({
        deliveryIds: selectedDeliveries,
        isDelivered,
        isCollected
      })
      setSelectedDeliveries([])
      setShowBulkActions(false)
      loadDeliveries()
    } catch (error) {
      console.error('Error updating delivery status:', error)
      showError(error)
    }
  }

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedDeliveries.length} deliveries?`)) {
      try {
        await deliveryApi.bulkDelete({
          deliveryIds: selectedDeliveries,
          forceDelete: true
        })
        setSelectedDeliveries([])
        setShowBulkActions(false)
        loadDeliveries()
      } catch (error) {
        console.error('Error deleting deliveries:', error)
        showError(error)
      }
    }
  }

  const handleExport = async () => {
    try {
      const params = {
        ...(filters.buildingId && { buildingId: filters.buildingId }),
        ...(filters.fromDate && { fromDate: filters.fromDate }),
        ...(filters.toDate && { toDate: filters.toDate }),
        ...(filters.deliveryType && { deliveryType: parseInt(filters.deliveryType) })
      }
      const response = await deliveryApi.export(params)

      // Create download URL
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `deliveries-${new Date().toISOString().split('T')[0]}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting deliveries:', error)
      showError(error)
    }
  }

  const getDeliveryTypeIcon = (type) => {
    switch (type) {
      case 1: return <Package className="w-4 h-4" />
      case 2: return <Mail className="w-4 h-4" />
      default: return <Truck className="w-4 h-4" />
    }
  }

  const getDeliveryTypeText = (type) => {
    switch (type) {
      case 1: return 'Package'
      case 2: return 'Mail'
      default: return 'Unknown'
    }
  }

  // Colors now handled by Badge and StatusBadge components

  const getStatusText = (delivery) => {
    if (delivery.isCollected) return 'Collected'
    if (delivery.isDelivered) return 'Delivered'
    return 'Pending'
  }

  const isOverdue = (delivery) => {
    if (delivery.isCollected || !delivery.isDelivered) return false
    const deliveryDate = new Date(delivery.deliveryDate)
    const now = new Date()
    const daysDiff = (now - deliveryDate) / (1000 * 60 * 60 * 24)
    return daysDiff > 7 // Consider overdue after 7 days
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Deliveries"
        subtitle={`${totalCount} total deliveries`}
        action={
          <div className="flex items-center space-x-3">
            {selectedDeliveries.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {selectedDeliveries.length} selected
                </span>
                <Button
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  variant="secondary"
                  icon={<MoreVertical className="h-4 w-4" />}
                >
                  Actions
                </Button>
              </div>
            )}
            <Button
              as={Link}
              to="/deliveries/stats"
              variant="secondary"
              icon={<BarChart3 className="h-4 w-4" />}
            >
              Stats
            </Button>
            <Button
              onClick={handleExport}
              variant="secondary"
              icon={<Download className="h-4 w-4" />}
            >
              Export
            </Button>
            <Button
              as={Link}
              to="/deliveries/create"
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
            >
              Log Delivery
            </Button>
          </div>
        }
      />

      {/* Bulk Actions Dropdown */}
      {showBulkActions && selectedDeliveries.length > 0 && (
        <Card>
          <CardBody>
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">Bulk Actions:</span>
              <Button
                onClick={() => handleBulkStatusUpdate(true, false)}
                variant="accent"
                size="small"
              >
                Mark as Delivered
              </Button>
              <Button
                onClick={() => handleBulkStatusUpdate(true, true)}
                variant="primary"
                size="small"
              >
                Mark as Collected
              </Button>
              <Button
                onClick={handleBulkDelete}
                variant="danger"
                size="small"
              >
                Delete Selected
              </Button>
              <Button
                onClick={() => setShowBulkActions(false)}
                variant="secondary"
                size="small"
              >
                Cancel
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Filter Tabs */}
      <Card>
        <CardBody className="px-6 py-0">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {[
              { key: 'all', label: 'All Deliveries' },
              { key: 'pending', label: 'Pending' },
              { key: 'awaiting-collection', label: 'Awaiting Collection' },
              { key: 'overdue', label: 'Overdue' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleActiveFilterChange(tab.key)}
                className={`${activeFilter === tab.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            <div className="flex-1">
              <SearchBar
                value={searchInput}
                onChange={handleSearch}
                onKeyDown={handleSearchSubmit}
                placeholder="Search deliveries by recipient, sender, or tracking... (Press Enter to search)"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <FormSelect
                value={filters.deliveryType}
                onChange={(e) => handleFilterChange('deliveryType', e.target.value)}
                options={[
                  { value: '', label: 'All Types' },
                  { value: '1', label: 'Package' },
                  { value: '2', label: 'Mail' }
                ]}
                className="min-w-[120px]"
              />
              <FormSelect
                value={filters.buildingId}
                onChange={(e) => handleFilterChange('buildingId', e.target.value)}
                options={[
                  { value: '', label: 'All Buildings' },
                  ...buildings.map(building => ({ value: building.id, label: building.name }))
                ]}
                className="min-w-[150px]"
              />
              <FormSelect
                value={filters.isDelivered}
                onChange={(e) => handleFilterChange('isDelivered', e.target.value)}
                options={[
                  { value: '', label: 'Delivered Status' },
                  { value: 'true', label: 'Delivered' },
                  { value: 'false', label: 'Not Delivered' }
                ]}
                className="min-w-[140px]"
              />
              <FormSelect
                value={filters.isCollected}
                onChange={(e) => handleFilterChange('isCollected', e.target.value)}
                options={[
                  { value: '', label: 'Collection Status' },
                  { value: 'true', label: 'Collected' },
                  { value: 'false', label: 'Not Collected' }
                ]}
                className="min-w-[150px]"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex gap-2">
              <FormField
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                placeholder="From Date"
              />
              <FormField
                type="date"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                placeholder="To Date"
              />
            </div>
            <Button
              onClick={() => {
                setFilters({
                  deliveryType: '',
                  buildingId: '',
                  isDelivered: '',
                  isCollected: '',
                  fromDate: '',
                  toDate: '',
                  recipientUserId: ''
                })
                setSearchInput('')
                setSearchTerm('')
              }}
              variant="secondary"
              icon={<Filter className="h-4 w-4" />}
            >
              Clear Filters
            </Button>
          </div>
        </CardBody>
      </Card>

      <ListContainer>
        {deliveries.length === 0 ? (
          <EmptyState
            icon={Package}
            title={`No ${activeFilter === 'all' ? '' : activeFilter} deliveries found`}
            description={searchTerm || Object.values(filters).some(f => f) ?
              "Try adjusting your search or filters." :
              "Get started by logging your first delivery."
            }
            action={
              <Button
                as={Link}
                to="/deliveries/create"
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
              >
                Log Delivery
              </Button>
            }
          />
        ) : (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>
                    <input
                      type="checkbox"
                      checked={selectedDeliveries.length === deliveries.length && deliveries.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </TableCell>
                  <TableCell header>Delivery</TableCell>
                  <TableCell header>Type</TableCell>
                  <TableCell header>Recipient</TableCell>
                  <TableCell header>Building</TableCell>
                  <TableCell header>Status</TableCell>
                  <TableCell header>Created Date</TableCell>
                  <TableCell header>Days Waiting</TableCell>
                  <TableCell header>
                    <span className="sr-only">Actions</span>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.map((delivery) => (
                  <TableRow key={delivery.deliveryId} clickable className={isOverdue(delivery) ? 'bg-red-50/50 hover:bg-red-50' : ''}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedDeliveries.includes(delivery.deliveryId)}
                        onChange={(e) => handleSelectDelivery(delivery.deliveryId, e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="text-sm font-semibold text-slate-900 group-hover:text-primary-600 transition-colors">
                          {delivery.serialNumber || `#${delivery.deliveryId}`}
                          {isOverdue(delivery) && (
                            <Badge variant="danger" className="ml-2">
                              Overdue
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-slate-500 mt-0.5">
                          {delivery.senderName || 'Unknown Sender'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={delivery.deliveryType === 1 ? 'primary' : 'accent'}
                        className="flex items-center w-fit"
                      >
                        {getDeliveryTypeIcon(delivery.deliveryType)}
                        <span className="ml-1.5">{getDeliveryTypeText(delivery.deliveryType)}</span>
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {delivery.recipientName || 'Unknown Recipient'}
                        </div>
                        <div className="text-sm text-slate-500 mt-0.5">
                          {delivery.recipientUnitNumber ? `Unit ${delivery.recipientUnitNumber}` : 'No Unit'}
                          {delivery.recipientFloorNumber && ` - Floor ${delivery.recipientFloorNumber}`}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-700">
                        {delivery.buildingName || 'Unknown Building'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={delivery.isCollected ? 'collected' :
                          delivery.isDelivered ? 'delivered' : 'pending'}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm text-slate-600">
                        <Calendar className="w-4 h-4 mr-1.5 text-slate-400" />
                        {formatDate(delivery.createDateTimeUtc)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-700 font-medium">
                        {delivery.daysWaiting || 0} days
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end pr-4">
                        <Button
                          as={Link}
                          to={`/deliveries/${delivery.deliveryId}`}
                          variant="ghost"
                          size="small"
                        >
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-between items-center px-2">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>
        )}
      </ListContainer>

      {/* Selected deliveries info */}
      {selectedDeliveries.length > 0 && (
        <Card className="bg-primary-50 border-primary-200">
          <CardBody>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="text-sm font-medium text-primary-900">
                  {selectedDeliveries.length} delivery{selectedDeliveries.length === 1 ? '' : 'ies'} selected
                </div>
              </div>
              <Button
                onClick={() => setSelectedDeliveries([])}
                variant="ghost"
                size="small"
              >
                Clear selection
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}

export default Deliveries